// crm-client.js — thin, DOM-free CRM transport (Brief 188).
//
// Talks to the CRM's web contract (Swagger UI at `${CRM_HOST}/api-docs/`).
// Rule #1 hygiene: no DOM, no imports from src/screens/ or src/ui/. The only
// project imports are auth-store.js (bearer token) and config/crm-host.js
// (build-time host). Session-invalidation surfaces via the onSessionInvalid()
// callback seam below — this module never touches the router or the DOM.
//
// Every exported function resolves a CrmResult; NONE of them throw for a
// CRM-level failure (bad credentials, a network error, an unexpected HTTP
// status) — callers branch on `result.success`. The one exception is
// crmStartTrial(), which throws synchronously because the endpoint isn't
// wired yet (see its docblock) — that's a programmer-error guard, not a
// CRM-level outcome.
//
// Web callers authenticate with `Authorization: Bearer` and must NEVER send
// `uaid` or `device_time` (every web endpoint states this) — request() below
// is the one place a body is built, and neither field is ever added there.

import { CRM_HOST } from "../config/crm-host.js";
import { getSession } from "./auth-store.js";

// Swagger displays paths as `/app/...`; the OpenAPI basePath is almost
// certainly `/api/v1` (unconfirmed — see Brief 188 §"Pre-flight status").
// If the live spec's servers/basePath block differs, change only this
// constant.
const CRM_BASE = `${CRM_HOST}/api/v1`;

/**
 * Normalised result every exported function resolves to. Never throws for a
 * CRM-level failure — network/parse failures also resolve (never reject) as
 * { success:false, error }. Callers branch on `success`.
 * @typedef {{ success: boolean, data?: any, error?: CrmError }} CrmResult
 * @typedef {{ code: string|number, message: string, kind: CrmErrorKind, data?: any, retryAfter?: string | null }} CrmError
 * @typedef {"bad_credentials"|"session_displaced"|"progress_conflict"|"network"|"generic"|"validation_error"|"account_locked"|"rate_limited"} CrmErrorKind
 */

/** @type {Array<() => void>} */
let sessionInvalidListeners = [];

/**
 * Register a callback invoked whenever a response normalises to
 * `kind:"session_displaced"` (401, or 409/91049). Callers (route guard,
 * settings screen, sync engine) use this to clear the session and route to
 * /login — crm-client itself never touches the DOM or the router.
 * @param {() => void} cb
 * @returns {() => void} unsubscribe
 */
export function onSessionInvalid(cb) {
  sessionInvalidListeners.push(cb);
  return () => {
    sessionInvalidListeners = sessionInvalidListeners.filter((c) => c !== cb);
  };
}

function notifySessionInvalid() {
  for (const cb of sessionInvalidListeners) {
    try { cb(); } catch (e) { console.error("[crm-client] onSessionInvalid listener threw", e); }
  }
}

/**
 * The ONLY place password encoding happens for web login. Confirmed
 * 2026-07-15 (Chris): `/app/auth/web-login` expects the RAW password over
 * TLS, not MD5 — this differs from iOS's KFELoginManager.m:296 MD5, which is
 * fine, since web-login is a new web-only endpoint. Kept as its own function
 * (rather than inlined) so a future change is one line.
 * @param {string} raw
 * @returns {string}
 */
function preparePassword(raw) {
  return raw;
}

/**
 * The one HTTP chokepoint. Attaches Authorization: Bearer for authed calls,
 * form-encodes or JSON-encodes the body, and maps transport/HTTP outcomes to
 * a CrmResult. Web NEVER sends uaid or device_time — neither field is ever
 * part of `form`/`json`/`query` in any exported function below.
 * @param {{
 *   method: string,
 *   path: string,
 *   auth?: boolean,
 *   form?: Record<string, unknown>,
 *   json?: Record<string, unknown>,
 *   query?: Record<string, unknown>,
 *   isLogin?: boolean,
 *   keepalive?: boolean,
 * }} opts
 * @returns {Promise<CrmResult>}
 */
async function request(opts) {
  const { method, path, auth = false, form, json, query, isLogin = false, keepalive = false } = opts;

  /** @type {Record<string, string>} */
  const headers = {};

  if (auth) {
    const token = getSession()?.token;
    if (!token) {
      // No session to attach — resolve without a network call (brief §0).
      return { success: false, error: { kind: "session_displaced", code: 20000, message: "No active session" } };
    }
    headers.Authorization = `Bearer ${token}`;
  }

  let url = `${CRM_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams(/** @type {Record<string,string>} */ (query)).toString();
    if (qs) url += `?${qs}`;
  }

  /** @type {string | undefined} */
  let body;
  if (form) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
    body = new URLSearchParams(/** @type {Record<string,string>} */ (form)).toString();
  } else if (json) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(json);
  }

  let res;
  try {
    // `keepalive` (Brief 025) lets an unload-time flush outlive the page. It is
    // the ONLY unload transport that can carry `Authorization: Bearer` —
    // navigator.sendBeacon cannot set headers at all, and putting a JWT in a
    // query string is not acceptable. Browsers cap a keepalive body at ~64 KB,
    // which is the same ceiling sendBeacon has; sync-engine keeps batches well
    // under it by flushing at every breakpoint.
    res = await fetch(url, keepalive ? { method, headers, body, keepalive } : { method, headers, body });
  } catch (err) {
    return {
      success: false,
      error: { kind: "network", code: "network", message: err instanceof Error ? err.message : String(err) },
    };
  }

  let payload;
  try {
    payload = await res.json();
  } catch {
    // Response body isn't JSON. 429 uses Laravel's default format (not the
    // app's JSON shape — Allen confirmed 2026-08-12) — map it before falling
    // back to a generic parse-fail.
    if (res.status === 429) {
      return { success: false, error: { kind: "rate_limited", code: 429, message: "Too many requests, please wait", retryAfter: res.headers.get("Retry-After") } };
    }
    return { success: false, error: { kind: "network", code: "parse", message: "Could not parse CRM response" } };
  }

  // Status code mapping. Contract confirmed with Allen (CRM owner) 2026-08-12:
  //   401 → invalid/missing/expired token (20000)  → session_displaced
  //   403 → invalid credentials (20002)            → bad_credentials
  //   409 → 91049 session displaced OR 91048 unpushed-progress conflict
  //   400 → validation error (10002) OR expired license (91012) → validation_error
  //         (differentiated by error_code + server message; login-time
  //         bad-password is 403, NOT 400)
  //   423 → account locked after too many failed logins (20005) → account_locked
  //   429 → rate limited (200/min per IP/account) → rate_limited
  if (res.status === 401) {
    notifySessionInvalid();
    return { success: false, error: { kind: "session_displaced", code: 20000, message: payload?.message ?? "Unauthorized" } };
  }

  if (res.status === 403) {
    return { success: false, error: { kind: "bad_credentials", code: payload?.error_code ?? 20002, message: payload?.message ?? "Invalid credentials" } };
  }

  if (res.status === 409) {
    const errorCode = payload?.error_code;
    if (errorCode === 91049) {
      notifySessionInvalid();
      return { success: false, error: { kind: "session_displaced", code: 91049, message: payload?.message ?? "Session displaced" } };
    }
    if (errorCode === 91048) {
      // Unpushed-progress conflict — preserve the full body (options,
      // gap_seconds, previous_device, session_resolution) for the future
      // device-switch UI (Brief 029) to drive its three-option flow.
      return { success: false, error: { kind: "progress_conflict", code: 91048, message: payload?.message ?? "Progress conflict", data: payload } };
    }
    return { success: false, error: { kind: "generic", code: errorCode ?? 409, message: payload?.message ?? "Conflict" } };
  }

  if (res.status === 423) {
    return { success: false, error: { kind: "account_locked", code: payload?.error_code ?? 20005, message: payload?.message ?? "Account locked" } };
  }

  if (res.status === 429) {
    // Some Laravel configs DO return JSON for 429 — handle it here too.
    return { success: false, error: { kind: "rate_limited", code: 429, message: payload?.message ?? "Too many requests, please wait", retryAfter: res.headers.get("Retry-After") } };
  }

  if (res.status === 400) {
    // 400 covers validation errors and expired license — NOT bad credentials
    // (that's 403). Login callers get validation_error (not bad_credentials)
    // so the UX message reflects what actually failed. isLogin retained on
    // the request() opt only so future login-specific 400 semantics have a
    // seam to hang off.
    return {
      success: false,
      error: { kind: "validation_error", code: payload?.error_code ?? 400, message: payload?.message ?? "Bad request" },
    };
  }

  if (!res.ok) {
    return { success: false, error: { kind: "generic", code: payload?.error_code ?? res.status, message: payload?.message ?? `HTTP ${res.status}` } };
  }

  if (payload?.success === true || payload?.success === 1) {
    return { success: true, data: payload.data ?? payload };
  }

  // 2xx with no explicit success flag — tolerate it as success rather than
  // rejecting a response the spec's basePath uncertainty (Brief 188
  // §"Pre-flight status") might shape slightly differently than expected.
  return { success: true, data: payload };
}

// ---------------------------------------------------------------------------
// 1. Auth
// ---------------------------------------------------------------------------

/**
 * Normalise the web-login payload's field casing (CRM uses snake_case, e.g.
 * `expires_in`, `license_end_date`) into the camelCase shape the rest of the
 * app consumes. Tolerant of either casing since the exact wire shape is
 * unconfirmed until the live spec is read (Brief 188 §"Pre-flight status").
 * @param {any} raw
 * @returns {any}
 */
function normaliseLoginData(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const user = raw.user;
  const session = raw.session;
  const device = raw.device;
  return {
    token: raw.token ?? raw.access_token,
    tokenType: raw.token_type ?? raw.tokenType,
    expiresIn: raw.expires_in ?? raw.expiresIn,
    user: user ? {
      id: user.id,
      username: user.username,
      name: user.name,
      licenseNum: user.license_num ?? user.licenseNum,
      licenseEndDate: user.license_end_date ?? user.licenseEndDate,
    } : undefined,
    session: session ? {
      id: session.id,
      clientType: session.client_type ?? session.clientType,
    } : undefined,
    device: device ? {
      customerDeviceId: device.customer_device_id ?? device.customerDeviceId,
      uaid: device.uaid,
      deviceModel: device.device_model ?? device.deviceModel,
    } : undefined,
  };
}

/**
 * The primary login path (replaces Brief 148's crmActivate-for-login
 * assumption). On success the CALLER (kfe-login.js) writes the session via
 * auth-store.setSession(data) — this function returns the normalised payload
 * but does not persist it.
 *
 * Body is exactly {username, password} — Allen confirmed 2026-08-12 the server
 * takes only these two fields (no language_code, no client_type). Form-encoded
 * works (Laravel accepts both form and JSON for this endpoint); form kept for
 * consistency with the rest of the auth surface.
 * @param {{ username: string, password: string }} params
 * @returns {Promise<CrmResult>}
 */
export async function crmWebLogin({ username, password }) {
  const result = await request({
    method: "POST",
    path: "/app/auth/web-login",
    isLogin: true,
    form: { username, password: preparePassword(password) },
  });
  if (!result.success) return result;
  return { success: true, data: normaliseLoginData(result.data) };
}

/**
 * Invalidate the server-side session. Does not clear the local auth-store
 * session — the caller does that (mirrors crmWebLogin not writing it).
 * @returns {Promise<CrmResult>}
 */
export async function crmLogout() {
  return request({ method: "DELETE", path: "/app/auth/logout", auth: true });
}

// ---------------------------------------------------------------------------
// 2. Forgot-password
// ---------------------------------------------------------------------------

/**
 * @param {{ username: string }} params
 * @returns {Promise<CrmResult>}
 */
export async function crmCheckUsername({ username }) {
  return request({ method: "GET", path: "/app/check-username", query: { username } });
}

/**
 * @param {{ username: string, sendVia: 1|2|3 }} params 1=SMS, 2=email, 3=both
 * @returns {Promise<CrmResult>}
 */
export async function crmGenerateOtp({ username, sendVia }) {
  return request({ method: "POST", path: "/app/generate-otp", form: { username, send_via: sendVia } });
}

// ---------------------------------------------------------------------------
// 3. Trial / entitlement
// ---------------------------------------------------------------------------

/**
 * @param {{ login: string }} params username or email
 * @returns {Promise<CrmResult>}
 */
export async function crmCheckMember({ login }) {
  return request({ method: "GET", path: "/app/check-member", auth: true, query: { login } });
}

/** @returns {Promise<CrmResult>} */
export async function crmSubscriptionStatus() {
  return request({ method: "GET", path: "/app/subscription/status", auth: true });
}

/**
 * NOT WIRED. The staging snapshot has no `/app/activate-trial` — only
 * `/app/subscribe` + `/app/check-member` exist, and neither is confirmed as
 * the "Start free trial" CTA's target. Do not guess it onto `/app/subscribe`
 * (Brief 188 §3). Throws so an accidental call fails loud instead of silently
 * hitting the wrong endpoint. TODO: confirm the trial-signup endpoint with
 * the CRM owner, then implement for real.
 * @returns {never}
 */
export function crmStartTrial() {
  throw new Error("crmStartTrial: not wired — trial-signup endpoint unconfirmed (Brief 188 §3).");
}

// ---------------------------------------------------------------------------
// 4. Session-conflict / device-switch transport (UI is a separate brief)
// ---------------------------------------------------------------------------

/**
 * Also doubles as the session heartbeat.
 * @param {{ sessionResolution?: "wait"|"accept_loss"|"reconcile" }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmTouchBase({ sessionResolution } = {}) {
  return request({
    method: "POST",
    path: "/app/touch-base",
    auth: true,
    json: sessionResolution ? { session_resolution: sessionResolution } : {},
  });
}

/**
 * Last-gasp: convert a pending reconciliation to accept_loss. Returns
 * `converted:0` in `data` when nothing was pending.
 * @returns {Promise<CrmResult>}
 */
export async function crmAcceptLoss() {
  return request({ method: "POST", path: "/app/session/accept-loss", auth: true, json: {} });
}

// ---------------------------------------------------------------------------
// 5. Sync transport — signatures only. Wiring (outbox/inbox/reducer) is the
//    sync-engine brief's job (Brief 025's successor); these are thin
//    request() wrappers so that brief has a stable contract to build against.
// ---------------------------------------------------------------------------

/**
 * @param {Record<string, unknown>} progress
 * @param {{ keepalive?: boolean }} [opts] set on the unload flush path (Brief 025)
 * @returns {Promise<CrmResult>}
 */
export async function crmAddLearnerProgress(progress, { keepalive = false } = {}) {
  return request({ method: "POST", path: "/app/add-learner-progress", auth: true, json: progress, keepalive });
}

/**
 * @param {Record<string, unknown>} progress
 * @param {{ keepalive?: boolean }} [opts] set on the unload flush path (Brief 025)
 * @returns {Promise<CrmResult>}
 */
export async function crmUpdateLearnerProgress(progress, { keepalive = false } = {}) {
  return request({ method: "POST", path: "/app/update-learner-progress", auth: true, json: progress, keepalive });
}

/**
 * `download_type` is required by the server (2026-09-14 live smoke: 400
 * "The download type field is required" without it). iOS canon at
 * `KFESyncUserDataMgr.m:143-160`:
 *   • 1 = fresh install OR "Download all my progress data since ever" ON
 *         → server returns FULL history
 *   • 0 = normal path with a valid last-sync cursor
 *         → server returns delta since that cursor
 * Web mirrors this: no local cursor yet → send 1 (full pull); have a cursor
 * from a prior successful pull → send 0 (delta).
 * @param {{ downloadType?: 0 | 1 }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmQueryLearnerProgress({ downloadType = 0 } = {}) {
  return request({
    method: "GET",
    path: "/app/query-learner-progress",
    auth: true,
    query: { download_type: downloadType },
  });
}

/**
 * `download_type` mirrors `crmQueryLearnerProgress` (iOS uses the same param
 * dict for both calls — `KFESyncUserDataMgr.m:143-160`). Defaults to 0 (delta);
 * caller passes 1 when there's no local cursor / fresh install.
 * @param {{ since?: string|number, downloadType?: 0 | 1 }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmDownloadLearnerProgress({ since, downloadType = 0 } = {}) {
  const query = { download_type: downloadType };
  if (since != null) query.since = since;
  return request({ method: "GET", path: "/app/download-learner-progress", auth: true, query });
}

/**
 * @param {{ since: string|number }} params
 * @returns {Promise<CrmResult>}
 */
export async function crmUpdateLastSync({ since }) {
  return request({ method: "PATCH", path: "/app/update-last-sync", auth: true, json: { since } });
}

/**
 * @param {{ since?: string|number }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmContentUpdate({ since } = {}) {
  return request({ method: "POST", path: "/app/content-update", auth: true, json: { since: since ?? 0 } });
}
